import {Component, Input} from '@angular/core';
import {DateTime} from 'luxon';
import {IActiveGameInstance} from "../../interfaces/IActiveGameInstance";
import {forkJoin, map, Observable, of, tap, timer} from "rxjs";
import {UIStateEnum} from "../../enum/UIStateEnum";
import Chart, {ChartConfiguration} from "chart.js/auto";
import {CountdownService} from "../../services/countdown.service";
import {ReportingService} from "../../services/reporting.service";
import {AsyncPipe, DatePipe, NgIf} from "@angular/common";
import {IManualAndAutoPlayTickets} from "../../interfaces/reporting/IManualAndAutoPlayTickets";
import {FormatCurrencyPipe} from "../../pipes/format-currency.pipe";
import {ISalesPerDay} from "../../interfaces/reporting/ISalesPerDay";
import {ICharitySalesPerDraw} from "../../interfaces/reporting/ICharitySalesPerDraw";
import {
  ComponentLevelErrorMessageComponent
} from "../display-errors/component-level-error-message/component-level-error-message.component";
import {PipesModule} from "../../pipes/pipes.module";
import {PermissionTypes} from "../../enum/PermissionTypes";
import {HasPermissionDirective} from "../../directives/permissions/has-permission";
import {MatTableModule} from "@angular/material/table";
import {MatButtonModule} from "@angular/material/button";
import {GameInstanceService} from "../../services/game-instance.service";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {IUnclaimedWinnersResult} from "../../interfaces/IUnclaimedWinnersResult";
import {IActiveGameInstanceWithTotals} from "../../interfaces/IActiveGameInstanceWithTotals";
import {PermissionsService} from "../../services/permissions.service";
import {IPlayedAndNotPlayedPerInstance} from "../../interfaces/reporting/IPlayedAndNotPlayedPerInstance";
import {IPlayersPerDateRange} from "../../interfaces/reporting/IPlayersPerDateRange";
import {GameService} from "../../services/game.service";
import {IGameQueryResult} from "../../interfaces/IGameQueryResult";
import {GameStateEnum} from "../../enum/GameStateEnum";
import {GameTypeEnum} from "../../enum/GameTypeEnum";

@Component({
  selector: 'app-display-active-game-instance',
  standalone: true,
  templateUrl: './display-active-game-instance.component.html',
  imports: [
    ComponentLevelErrorMessageComponent,
    DatePipe,
    PipesModule,
    HasPermissionDirective,
    MatTableModule,
    AsyncPipe,
    NgIf,
    MatButtonModule,
    MatProgressSpinner
  ],
  styleUrls: ['./display-active-game-instance.component.scss']
})
export class DisplayActiveGameInstanceComponent {

  @Input() set setSelectedGameInstance(gameInstanceP: IActiveGameInstance) {
    this.errorMessage = '';
    this.selectedGameInstance = gameInstanceP;
    this.determineApiCallsToMakeBasedOnPermissions();
  }

  public selectedGameInstance!: IActiveGameInstance;
  public errorMessage: string = '';
  public manualAutoPlayChart!: Chart;
  public ticketsChart!: Chart;
  public playersTicketsLineChart!: Chart;
  public salesPerDayLineChart!: Chart;
  public charitySalesBarGraph!: Chart;
  public endDateCountDown$: Observable<string> = of('');
  public uiState = UIStateEnum.ShowData;
  public uiStateForTemplate = UIStateEnum;
  public winnersPendingDecisions$: Observable<IUnclaimedWinnersResult> = of({UnclaimedWinners: []});
  public columnsToDisplay = ["gameInstanceId", "playerNumberId", "playerWinnings", "playerName", "playerEmail",
    "createdOn"];
  public game: IGameQueryResult | null = null;

  protected readonly PermissionTypes = PermissionTypes;
  protected readonly GameStateEnum = GameStateEnum;

  constructor(private countdownService: CountdownService,
              private datePipe: DatePipe,
              private formatCurrencyPipe: FormatCurrencyPipe,
              private reportingService: ReportingService,
              private gameInstanceService: GameInstanceService,
              private permissionService: PermissionsService,
              private gameService: GameService) {
  }

  private determineApiCallsToMakeBasedOnPermissions() {
    const apiCalls = [];

    const endDate = DateTime.now().toISODate()!;
    const startDate = DateTime.now().minus({days: 30}).toISODate()!;

    apiCalls.push(this.gameService.getGameByGameId(this.selectedGameInstance!.GameId));

    if (this.permissionService.userHasPermission(PermissionTypes.ViewReports)) {
      apiCalls.push(
        this.reportingService.topTenCharityContributors(this.selectedGameInstance.GameId, this.selectedGameInstance.Id)
          .pipe(tap((charityContributors) => this.generateTopCharityContributors(charityContributors))),

        forkJoin([
          this.reportingService.getPlayedVsNotPlayedTicketsForDateRange(this.selectedGameInstance.GameId, startDate, endDate),
           this.reportingService.getPlayersPerInstanceForDateRange(this.selectedGameInstance.GameId, startDate, endDate)
        ]).pipe(
          tap(([ticketData, playersData]) => {
            this.generatePlayersTicketsOverTimeLineChart(ticketData, playersData);
          })
        ),
        this.reportingService.getManualVsAutoPlayTickets(this.selectedGameInstance.GameId, this.selectedGameInstance.Id)
          .pipe(tap((manualAutoplayTicketP) => this.generateManualVsAutoPlayChart(manualAutoplayTicketP))),
        this.reportingService.salesPerDay(this.selectedGameInstance.GameId, this.selectedGameInstance.Id)
          .pipe(tap((salesPerDayP) => this.generateSalesPerDayChart(salesPerDayP)))
      )
    }
    if (this.permissionService.userHasPermission(PermissionTypes.ViewGameSummaryReport)) {
      apiCalls.push(this.gameInstanceService.fetchActiveGameInstanceByGameWithTotals(this.selectedGameInstance.GameId)
        .pipe(tap((gameInstanceWithTotalsP) => this.generateTicketChart(gameInstanceWithTotalsP))))
    }

    forkJoin(apiCalls)
      .subscribe({
        next: () => {
          this.createCountdownSubscription(new Date(this.selectedGameInstance.EndedOn));
          this.game = this.gameService.activeGame();
        },
        error: () => {
          this.errorMessage = 'Looks like there was an issue retrieving data for this draw.' +
            ' Please reload the page or reach out to a system administrator.'
        }
      })
  }

  private generateTopCharityContributors(charitiesTotalsP: ICharitySalesPerDraw[]) {
    if (this.charitySalesBarGraph) {
      this.charitySalesBarGraph.destroy();
    }

    if (charitiesTotalsP.length === 0) {
      return;
    }

    let salesCntxt = document.getElementById('charitySalesBarGraph') as HTMLCanvasElement;
    const salesData = {
      labels: charitiesTotalsP.map((perCharity) => perCharity.CharityName),
      datasets: [
        {
          label: 'Top Charity Contributors',
          data: charitiesTotalsP.map((perCharity) => perCharity.Sales),
          fill: false,
          backgroundColor: [
            'rgba(255, 99, 132, 0.2)',
            'rgba(255, 159, 64, 0.2)',
            'rgba(255, 205, 86, 0.2)',
            'rgba(75, 192, 192, 0.2)',
            'rgba(54, 162, 235, 0.2)',
            'rgba(153, 102, 255, 0.2)',
            'rgba(201, 203, 207, 0.2)'
          ],
          borderColor: [
            'rgb(255, 99, 132)',
            'rgb(255, 159, 64)',
            'rgb(255, 205, 86)',
            'rgb(75, 192, 192)',
            'rgb(54, 162, 235)',
            'rgb(153, 102, 255)',
            'rgb(201, 203, 207)'
          ],
          borderWidth: 1
        }
      ]
    };

    const config: ChartConfiguration = {
      type: 'bar',
      data: salesData,
      options: {
        scales: {
          y: {
            ticks: {
              callback: (value) => {
                return this.formatCurrencyPipe.transform(Number(value));
              }
            }
          }
        },
        responsive: true,
        plugins: {
          tooltip: {
            callbacks: {
              label: (context: any) => {
                return this.formatCurrencyPipe.transform(context.raw);
              }
            }
          },
          legend: {
            position: 'top',
          },
          title: {
            display: true,
            text: 'Top Charity Contributors '
          }
        }
      }
    };

    if (salesCntxt) {
      this.charitySalesBarGraph = new Chart(salesCntxt, config)
    }
  }

  private generateSalesPerDayChart(salesPerDayP: ISalesPerDay[]) {
    if (this.salesPerDayLineChart) {
      this.salesPerDayLineChart.destroy();
    }

    if (salesPerDayP.length === 0) {
      return;
    }

    salesPerDayP = salesPerDayP.sort((a, b) => a.SaleDate < b.SaleDate ? -1 : 1);

    let salesCntxt = document.getElementById('salesPerDayLineChart') as HTMLCanvasElement;

    const salesData = {
      labels: salesPerDayP.map((perDay) => this.datePipe.transform(perDay.SaleDate, 'MMM d', 'GMT')),
      datasets: [
        {
          label: 'Tickets Sold',
          data: salesPerDayP.map((perDay) => perDay.TicketsSold),
          fill: false,
          borderColor: 'rgb(75, 192, 192)',
          tension: 0.1
        }
      ]
    };

    const config: ChartConfiguration = {
      type: 'line',
      data: salesData,
      options: {
        responsive: true,
        plugins: {
          legend: {
            position: 'top',
          },
          title: {
            display: true,
            text: 'Sales Per Day'
          }
        }
      }
    };

    if (salesCntxt) {
      this.salesPerDayLineChart = new Chart(salesCntxt, config)
    }

  }

  private generatePlayersTicketsOverTimeLineChart(
    ticketData: IPlayedAndNotPlayedPerInstance[],
    playerData: IPlayersPerDateRange[]) {

    if (this.playersTicketsLineChart) {
      this.playersTicketsLineChart.destroy();
    }

    if (ticketData.length === 0 || playerData.length === 0) {
      return;
    }

    playerData = playerData.sort((a, b) => new Date(a.DrawDate) < new Date(b.DrawDate) ? -1 : 1);
    const ticketMap = new Map(ticketData.map(item => [item.GameInstanceId, item]));

    let playersCntxt = document.getElementById('playersTicketsLineChart') as HTMLCanvasElement;

    const dataPoints = playerData.filter(player => ticketMap.has(player.GameInstanceId));

    const playersData = {
      labels: dataPoints.map(player => this.datePipe.transform(player.DrawDate, 'MMM d')),
      datasets: [
        {
          label: 'Players in draw',
          data: dataPoints.map(player => player.PlayerCount),
          fill: false,
          borderColor: 'rgb(75, 192, 192)',
          tension: 0.1
        },
        {
          label: 'Tickets in draw',
          data: dataPoints.map(player => ticketMap.get(player.GameInstanceId)!.PurchasedTickets),
          fill: false,
          borderColor: 'rgb(125, 100, 150)',
          tension: 0.1
        }
      ]
    };

    const config: ChartConfiguration = {
      type: 'line',
      data: playersData,
      options: {
        responsive: true,
        plugins: {
          legend: {
            position: 'top',
          },
          title: {
            display: true,
            text: 'Players/Tickets Over The Last 30 Days'
          }
        }
      }
    };

    if (playersCntxt) {
      this.playersTicketsLineChart = new Chart(playersCntxt, config)
    }
  }

  public generateManualVsAutoPlayChart(manualAutoPlayReportP: IManualAndAutoPlayTickets) {
    if (this.manualAutoPlayChart) {
      this.manualAutoPlayChart.destroy();
    }

    if (!manualAutoPlayReportP.AutoPlayedTicketsSales && !manualAutoPlayReportP.ManuallyPurchasedTicketsSales) {
      return;
    }

    let ticketsCntxt = document.getElementById('manualAutoPlayChart') as HTMLCanvasElement;

    const config: any = {
      type: 'pie',
      data: {
        datasets: [{
          data: [
            manualAutoPlayReportP.AutoPlayedTicketsSales,
            manualAutoPlayReportP.ManuallyPurchasedTicketsSales],
          backgroundColor: [
            'rgb(255, 205, 86)',
            'rgb(54, 162, 235)',
          ],
        }],
        labels: ['Auto Play Sales ($)', 'Manual Sales ($)'],
      },
      options: {
        responsive: true,
        plugins: {
          tooltip: {
            callbacks: {
              label: (context: any) => {
                return this.formatCurrencyPipe.transform(context.parsed);
              }
            }
          },
          legend: {
            position: 'top',
          },
          title: {
            display: true,
            text: 'Autoplay vs Manual Ticket Sales'
          }
        }
      }
    };

    if (ticketsCntxt) {
      this.manualAutoPlayChart = new Chart(ticketsCntxt, config);
    }

  }

  public generateTicketChart(gameInstanceWithTotalsP: IActiveGameInstanceWithTotals) {
    if (this.ticketsChart) {
      this.ticketsChart.destroy();
    }

    if (!gameInstanceWithTotalsP.TotalPlayedTickets && !gameInstanceWithTotalsP.TotalUnplayedTickets) {
      return;
    }

    let ticketsCntxt = document.getElementById('ticketsChart') as HTMLCanvasElement;

    // A 50/50 ticket is bought for one draw, so there is no unplayed ticket to show beside it.
    const has_unplayed = gameInstanceWithTotalsP.Type === GameTypeEnum.GoldRush;
    const ticketsData = {
      labels: has_unplayed ? ['Played Tickets', 'Unplayed Tickets'] : ['Played Tickets'],
      datasets: [
        {
          data: has_unplayed
            ? [gameInstanceWithTotalsP.TotalPlayedTickets, gameInstanceWithTotalsP.TotalUnplayedTickets]
            : [gameInstanceWithTotalsP.TotalPlayedTickets],
          backgroundColor: ['#9BD0F5', '#e25d22'],
        }
      ]
    };

    const config: ChartConfiguration = {
      plugins: [],
      type: 'pie',
      data: ticketsData,
      options: {
        responsive: true,
        plugins: {
          legend: {
            position: 'top',
          },
          title: {
            display: true,
            text: 'Tickets'
          }
        }
      }
    };

    if (ticketsCntxt) {
      this.ticketsChart = new Chart(ticketsCntxt, config);
    }

  }

  private createCountdownSubscription(dateP: Date): void {
    this.endDateCountDown$ = timer(0, 1000).pipe(map(() => {
      const end_date = dateP;
      if (end_date) {
        return this.countdownService.formatRemainingTimeUntilDraw(end_date);
      }

      return '';
    }));
  }
}
